import useEmblaCarousel from 'embla-carousel-react';
import {useState, useCallback} from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import {useInView} from 'react-intersection-observer';

import type {SanityModuleVideoSlider} from '../../types';
import VideoPopup from '../ui/VideoPopup.client';

import {isMobile} from '../ui/animationVars';
import {gridVars} from '../ui/animationVars';
import {contentVars} from '../ui/animationVars';
import {Image, Link} from '@shopify/hydrogen';
import slugify from 'slugify';

const LinkSwitch = ({cta, children, url, setIsOpen, ctaLink}: any) => {
  switch (cta) {
    case true:
      return (
        <motion.div
          variants={contentVars}
          className="relative z-10 mr-12 w-full max-w-[410px] cursor-pointer overflow-hidden rounded-xl"
          style={{flex: '0 0 auto'}}
        >
          <Link to={ctaLink.slug}>{children}</Link>
        </motion.div>
      );

    default:
      return (
        <motion.div
          variants={contentVars}
          onClick={() => setIsOpen(url)}
          className="relative z-10 mr-12 w-full max-w-[410px] cursor-pointer overflow-hidden rounded-xl"
          style={{flex: '0 0 auto'}}
        >
          {children}
        </motion.div>
      );
  }
};

const VideoCard = ({heading, url, thumbnail, setIsOpen, cta, ctaLink}: any) => {
  return (
    <LinkSwitch cta={cta} url={url} setIsOpen={setIsOpen} ctaLink={ctaLink}>
      <div className="aspect-h-6 aspect-w-4 relative w-full">
        <Image
          width="inherit"
          height="inherit"
          data={thumbnail}
          alt={heading}
          className="transition-standard absolute left-0 top-0 h-full w-full object-cover hover:scale-105"
        />
        <div className="pointer-events-none absolute left-0 top-0 h-full w-full bg-black bg-opacity-50 object-cover" />
      </div>
      <div className="w-fulln pointer-events-none absolute bottom-0 p-8">
        <h6 className="blockH6 text-white">{heading}</h6>
        <div className="mt-3 flex items-center gap-x-5">
          {!cta && (
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-orange bg-opacity-30">
              <svg
                width="11"
                height="12"
                viewBox="0 0 11 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.4687 5.80311L0.762867 11.4068L0.762868 0.199413L10.4687 5.80311Z"
                  fill="#F27021"
                />
              </svg>
            </div>
          )}
          <span className="blockH7 text-orange underline">
            {cta ? ctaLink?.title : 'Watch now'}
          </span>
        </div>
      </div>
    </LinkSwitch>
  );
};

export default function VideoSliderModule({
  module,
}: {
  module: SanityModuleVideoSlider;
}) {
  const {videos, heading}: any = module;

  /* Carousel & button handling */
  const [viewportRef, embla] = useEmblaCarousel({
    align: 'start',
    dragFree: false,
    loop: true,
  });

  const scrollNext = useCallback(() => embla && embla.scrollNext(), [embla]);

  const [isOpen, setIsOpen] = useState<string | boolean>(false);

  const {ref, inView} = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  return (
    <>
      <motion.div
        ref={ref}
        variants={gridVars}
        animate={!isMobile && (inView ? 'animate' : 'initial')}
        className="overflow-hidden bg-light-blue px-gutter py-space-s"
      >
        <div className="flex items-center justify-between">
          <motion.h1
            variants={contentVars}
            className="richH1 font-bold"
            data-testid={`section-${slugify(heading, {
              lower: true,
              trim: true,
            })}`}
          >
            {heading}
          </motion.h1>
          <div className="flex items-center gap-x-8">
            <motion.button
              variants={contentVars}
              onClick={() => scrollNext()}
              className="transition-standard"
              aria-label="Next"
            >
              <svg
                width="40"
                height="32"
                viewBox="0 0 40 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M39.9987 15.9998C39.985 15.4763 39.8276 15.1615 39.6262 14.9351L28.5538 1.30744C28.031 0.570146 26.8458 0.453114 26.1048 1.05197C25.364 1.65083 25.3248 2.77015 25.9189 3.43676L34.7289 14.2958H2.52358C1.58252 14.2958 0.820312 15.0586 0.820312 15.999C0.820312 16.9401 1.58314 17.7023 2.52358 17.7023H34.7289L25.9189 28.5613C25.2445 29.2344 25.4364 30.4144 26.1482 31.0477C26.6959 31.5349 28.1562 31.3798 28.5543 30.6906L39.6266 17.063C39.9148 16.7101 40.0027 16.4475 39.9991 15.9983L39.9987 15.9998Z"
                  fill="#4D4D4F"
                />
              </svg>
            </motion.button>
          </div>
        </div>
        <div ref={viewportRef} className="mt-space-s">
          <div className="flex flex-row items-center">
            {videos?.map((card: any, i: number) => (
              <VideoCard key={i} {...card} setIsOpen={setIsOpen} />
            ))}
          </div>
        </div>
      </motion.div>

      <AnimatePresence mode="wait">
        {isOpen && <VideoPopup url={isOpen} setIsOpen={setIsOpen} />}
      </AnimatePresence>
    </>
  );
}
